// src/components/SubmitTime/components/ReplyEditor.tsx
// Sub-component for reply editor and attachments

import React, { useRef, useEffect, useCallback, useState } from 'react';
import $ from 'jquery';
import { nanoid } from 'nanoid';
import ReactSummernoteCustom from '../../Globals/SummernoteCustom';
import type { SummernoteAPI } from '../../Globals/SummernoteCustom';
import { onSummernotePaste } from '../../Globals/summernotePasteUtils';
import ConversationApi from '../../ConversationItem/api/ConversationApi';
import CorgeeStorageHelper from '../../Globals/CorgeeStorageHelper';
import { apiService } from '@/services/api';
import { buildConversationDetailsUrl } from '@lib/conversationUrl';
import type { Attachment, LayerItem } from '../types';

// Module-level counter for temporary attachment IDs.
// Negative values guarantee no collision with real server-assigned IDs (always > 0).
let _tempIdCounter = 0;
const genTempId = (): number => --_tempIdCounter;

interface Person {
  Name: string;
  IsEnable: boolean;
  ConversationId: number;
  PersonId: number;
}

interface Card {
  ConversationId: number;
  Title: string;
  ProjectName: string;
}

interface ChatGroup {
  NameGroup: string;
  ParticipantsByID: string;
  ParticipantsByName: string;
}

interface ReplyEditorProps {
  body: string;
  attachments: Attachment[];
  canEdit: boolean;
  LayerItem?: LayerItem;
  onBodyChange: (body: string) => void;
  onAttachmentAdd: (attachment: Attachment) => void;
  onAttachmentRemove: (attachmentId: number) => void;
  validationError?: boolean;
  customKey?: number;
  // Hint / mention props (optional — enables @mention and [[card autocomplete)
  conversationId?: number;
  apiConfig?: { apiUrl?: string; token?: string };
  layers?: Array<{ LayerId: number }>;
  groupIdsSelected?: string;
  projectId?: number;
  configValueId?: number;
}

export const ReplyEditor: React.FC<ReplyEditorProps> = ({
  body,
  attachments,
  canEdit,
  LayerItem,
  onBodyChange,
  onAttachmentAdd,
  onAttachmentRemove,
  validationError = false,
  customKey = 0,
  conversationId,
  apiConfig,
  layers,
  groupIdsSelected,
  projectId,
  configValueId,
}) =>
{
  const fileInputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const summernoteRef = useRef<SummernoteAPI>(null);
  const isMountedRef = useRef(true);

  const [listPeopleName, setListPeopleName] = useState<Person[]>([]);
  const [groupName, setGroupName] = useState<ChatGroup[]>([]);

  // Stable refs so hint callbacks always see fresh data without re-creating options
  const getPeopleMentionsRef = useRef<() => Person[]>(() => []);
  const getCardsLinkedRef = useRef<() => Card[]>(() => []);
  const groupNameRef = useRef<ChatGroup[]>([]);

  useEffect(() =>
  {
    isMountedRef.current = true;
    return () =>
    {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() =>
  {
    groupNameRef.current = groupName;
  }, [groupName]);

  const getPeopleMentions = useCallback((): Person[] =>
  {
    return [...listPeopleName].sort((x, y) => Number(y.IsEnable) - Number(x.IsEnable));
  }, [listPeopleName]);

  useEffect(() =>
  {
    getPeopleMentionsRef.current = getPeopleMentions;
  }, [getPeopleMentions]);

  const getCardsLinked = useCallback((): Card[] =>
  {
    const cache = localStorage.getItem('C2_objStorageConversation');
    if (!cache) return [];
    try
    {
      const cards: Card[] = JSON.parse(cache);
      return cards.filter(c => c.ConversationId !== conversationId);
    }
    catch
    {
      return [];
    }
  }, [conversationId]);

  useEffect(() =>
  {
    getCardsLinkedRef.current = getCardsLinked;
  }, [getCardsLinked]);

  const getPeopleTagList = useCallback(async () =>
  {
    if (!conversationId || (layers?.length ?? 0) === 0)
    {
      setListPeopleName([]);
      setGroupName([]);
      return;
    }
    const api = {
      url: CorgeeStorageHelper.apiURL(apiConfig),
      token: apiConfig?.token ?? CorgeeStorageHelper.getToken(),
    };

    try
    {
      const data = await ConversationApi.getTagList(api, {
        ConversationId: conversationId,
        ProjectId: projectId,
        ConfigValueId: configValueId,
        LayerIdsSelected: layers?.map(l => l.LayerId).join(',') ?? '',
        GroupIdsSelected: groupIdsSelected ?? '',
      });

      if (!isMountedRef.current) return;

      const peopleName: Person[] = [];
      data.PeopleList.forEach((person: any) =>
      {
        if (person.PersonID !== null)
        {
          peopleName.push({
            Name: `(${person.PersonID}) ${person.Name}`,
            IsEnable: person.HasLayerPermissions,
            ConversationId: conversationId,
            PersonId: person.PersonID,
          });
        }
      });
      peopleName.sort((x, y) => Number(y.IsEnable) - Number(x.IsEnable));
      data.ChatGroups.forEach((group: any) =>
      {
        if (group.ParticipantsByID !== null)
        {
          peopleName.push({
            Name: group.NameGroup,
            IsEnable: true,
            ConversationId: conversationId,
            PersonId: parseInt(group.ParticipantsByID),
          });
        }
      });

      setListPeopleName(peopleName);
      setGroupName(data.ChatGroups);
    }
    catch (error)
    {
      console.error('ReplyEditor: error fetching tag list', error);
    }
  }, [conversationId, layers, apiConfig, groupIdsSelected, projectId, configValueId]);

  // Layer color styling
  useEffect(() =>
  {
    if (!LayerItem?.Color || LayerItem?.Color === 'transparent' || !wrapperRef.current) return;
    const el = $(wrapperRef.current);
    el.find('.note-editor.note-frame').css({ 'border-color': LayerItem?.Color });
    el.find('.note-toolbar').css({ 'background-color': LayerItem?.Color });
    el.find('.note-statusbar').css({ 'background-color': LayerItem?.Color });
    el.find('.note-status-output').css({ 'background-color': LayerItem?.Color });
    el.find('.note-resizebar').css({ 'background-color': LayerItem?.Color });
  }, [LayerItem, customKey]);

  // Fetch tag list whenever the selected layer changes
  useEffect(() =>
  {
    if (conversationId && (layers?.length ?? 0) > 0)
    {
      getPeopleTagList();
    }
  }, [conversationId, layers]);

  const onButtonAddCheckList = useCallback((context: any) =>
  {
    const ui = ($ as any).summernote.ui;
    const button = ui.button({
      // No tooltip option: custom buttons ignore options.tooltip
      contents: '<i class="fa fa-check-square-o" aria-label="Insert a Checklist"/>',
      click: () =>
      {
        const cbIdAux = `cb_${nanoid()}`;
        context.invoke(
          'editor.pasteHTML',
          `<span contentEditable="false"><input id="${cbIdAux}" type="checkbox" class="note-editor-checkbox"/></span>&nbsp;`,
        );
      },
    });
    return button.render();
  }, []);

  const updateBodyByDOM = useCallback(() =>
  {
    if (summernoteRef.current?.noteEditable)
    {
      const content = summernoteRef.current.noteEditable[0].innerHTML;
      onBodyChange(content);
    }
  }, [onBodyChange]);

  // Checklist inputs: `disabled` is a boolean attribute, so it must be removed to
  // be clickable. Change is delegated so inputs added later are covered too.
  useEffect(() =>
  {
    if (!wrapperRef.current) return;
    const wrapper = $(wrapperRef.current);

    wrapper.find('.note-editable .note-editor-checkbox').removeAttr('disabled');

    const onCheckboxChange = function (this: HTMLInputElement)
    {
      if (this.checked)
      {
        $(this).attr('checked', 'checked');
      }
      else
      {
        $(this).removeAttr('checked');
      }
      updateBodyByDOM();
    };
    wrapper.on('change', '.note-editor-checkbox', onCheckboxChange);

    return () =>
    {
      wrapper.off('change', '.note-editor-checkbox', onCheckboxChange);
    };
  }, [body, customKey, updateBodyByDOM]);

  const buildHint = useCallback(() =>
  {
    if (!conversationId) return undefined;

    return [
      {
        conversationId,
        mentions: [],
        match: /\B@([A-zÀ-ú0-9]*)$/,
        search: (keyword: string, callback: (items: Person[]) => void) =>
        {
          const kw = keyword.toLowerCase();
          setTimeout(() =>
          {
            if ($('.note-hint-popover').length > 0)
            {
              $('.core2, .corgee_com_card-body-info').on('scroll', () =>
              {
                $('.note-hint-popover').css('display', 'none');
              });
            }
          }, 100);

          const removeAccents = (str: string) => str.normalize('NFD').replace(/[̀-ͯ]/g, '');
          const available = getPeopleMentionsRef.current();
          callback(
            available.filter(item =>
              removeAccents(item.Name).toLowerCase().includes(removeAccents(kw)),
            ),
          );
        },
        template: (item: Person) =>
        {
          if (item.IsEnable) return item.Name;
          return `<div class="tag-not-available"><div>${item.Name}</div>(<span class="tag-not-available-text">This person does not have access to read the card</span>)</div>`;
        },
        content: (item: Person) =>
        {
          if (!item.IsEnable) return '';

          let isGroup = false;
          let participantsId: string[] = [];
          let participantsName: string[] = [];

          groupNameRef.current.forEach(group =>
          {
            if (group.NameGroup === item.Name)
            {
              isGroup = true;
              participantsId = group.ParticipantsByID.split(',');
              participantsName = group.ParticipantsByName.split(',');
            }
          });

          if (isGroup)
          {
            const spanParent = document.createElement('span');
            participantsId.forEach((id, i) =>
            {
              const blueSpan = document.createElement('span');
              $(blueSpan)
                .append(participantsName[i]!)
                .css('color', '#62AACB')
                .attr('person-id', id);
              const nextSpan = document.createElement('span');
              $(nextSpan).append($(blueSpan)).append('  ');
              $(spanParent).append($(nextSpan));
            });
            setTimeout(() => updateBodyByDOM(), 10);
            return spanParent;
          }

          const displayName = item.Name.replace(/^\(\d+\)\s*/, '');
          const blueSpan = document.createElement('span');
          $(blueSpan)
            .append(displayName)
            .css('color', '#62AACB')
            .css('font-weight', '600')
            .attr('person-id', item.PersonId);
          const nextSpan = document.createElement('span');
          $(nextSpan).append($(blueSpan)).append('  ');
          setTimeout(() => updateBodyByDOM(), 10);
          return nextSpan;
        },
      },
      {
        conversationId,
        mentions: [],
        match: /\B\[\[(\w*)$/,
        search: (keyword: string, callback: (items: Card[]) => void) =>
        {
          const kw = keyword.toLowerCase();
          setTimeout(() =>
          {
            if ($('.note-hint-popover').length > 0)
            {
              $('.core2, .corgee_com_card-body-info').on('scroll', () =>
              {
                $('.note-hint-popover').css('display', 'none');
              });
            }
          }, 100);

          const available = getCardsLinkedRef.current();
          callback(
            available.filter(item =>
              `${item.Title} - ${item.ProjectName}`.toLowerCase().includes(kw),
            ),
          );
        },
        template: (item: Card) =>
          item.ProjectName ? `${item.Title} - ${item.ProjectName}` : `${item.Title}`,
        content: (item: Card) =>
        {
          const url = buildConversationDetailsUrl(item.ConversationId);

          const label = item.ProjectName
            ? `${item.Title} - ${item.ProjectName}`
            : String(item.Title ?? '');

          const blueAtag = document.createElement('a');
          $(blueAtag)
            .append('[[', label, ']]')
            .attr('href', url)
            .attr('target', '_blank')
            .attr('link-conversation-id', String(item.ConversationId));

          const nextSpan = document.createElement('span');
          $(nextSpan).append($(blueAtag)).append(' ');
          setTimeout(() => updateBodyByDOM(), 10);
          return nextSpan;
        },
      },
    ];
  }, [conversationId, updateBodyByDOM]);

  const hint = buildHint();

  const summernoteOptions = {
    height: 140,
    toolbar: [
      ['style', ['bold', 'italic', 'underline']],
      ['font', ['fontname', 'fontsize']],
      ['color', ['color']],
      ['para', ['ul', 'ol', 'paragraph']],
      ['insert', ['link', 'picture']],
      ['customOptions', ['AddCheckList']],
    ],
    dialogsInBody: true,
    container: document.body,
    tooltip: false,
    disableResizeEditor: false,
    fontNames: [
      'Roboto', 'Arial', 'Arial Black', 'Comic Sans MS', 'Courier New',
      'Helvetica', 'Impact', 'Tahoma', 'Times New Roman', 'Verdana',
    ],
    // Roboto is a webfont; skip the installed-font check
    fontNamesIgnoreCheck: ['Roboto'],
    popover: { image: [], link: [], air: [] },
    buttons: {
      AddCheckList: onButtonAddCheckList,
    },
    ...(hint ? { hint } : {}),
  };

  const uploadFile = useCallback(
    async (file: File) =>
    {
      const tempId = genTempId();
      const dotIdx = file.name.lastIndexOf('.');
      const description = dotIdx > 0 ? file.name.substring(0, dotIdx) : file.name;
      const fileExt = dotIdx > 0 ? file.name.substring(dotIdx).toLowerCase() : '';

      onAttachmentAdd({
        AttachmentId: tempId,
        FileName: file.name,
        Description: description,
        FileExtension: fileExt,
        FileSize: file.size,
        FileType: file.type,
        FileUrl: '',
        isUploading: true,
      });

      try
      {
        const formData = new FormData();
        formData.append('file', file);
        const result = await apiService.post<{ FileName: string; TempFilePath: string }>(
          '/conversation/uploadfile',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        onAttachmentRemove(tempId);
        onAttachmentAdd({
          AttachmentId: tempId,
          FileName: result.FileName,
          Description: description,
          FileSize: file.size,
          FileType: file.type,
          FileContentType: file.type,
          FileExtension: fileExt,
          FileUrl: result.TempFilePath,
          TempFile: result.TempFilePath,
          isUploading: false,
        });
      }
      catch
      {
        onAttachmentRemove(tempId);
        alert(`Failed to upload "${file.name}". Please try again.`);
      }
    },
    [onAttachmentAdd, onAttachmentRemove],
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) =>
  {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    Array.from(files).forEach(uploadFile);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) =>
  {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer.files;
    if (!files || files.length === 0) return;
    Array.from(files).forEach(uploadFile);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) =>
  {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleImageUpload = useCallback(
    (files: File[]) =>
    {
      Array.from(files).forEach(uploadFile);
    },
    [uploadFile],
  );

  return (
    <div className="reply-editor" ref={wrapperRef}>
      {/* Summernote rich text editor */}
      <div
        className={`submit-time-editor-wrap ${validationError ? 'submit-time-editor-error' : ''}`}
      >
        <ReactSummernoteCustom
          ref={summernoteRef}
          key={customKey}
          value={body}
          onChange={onBodyChange}
          disabled={!canEdit}
          options={summernoteOptions}
          onImageUpload={handleImageUpload}
          onPaste={e => onSummernotePaste(e, () => summernoteRef.current?.noteEditable?.[0])}
        />
      </div>
      {validationError && (
        <span className="submit-required-field" style={{ fontSize: '11px' }}>
          Description is required (or add an attachment)
        </span>
      )}

      {/* File upload bar — files appear as pills inside this container */}
      <div
        className="corgee_com_card-add-replay-uploader submit-time-upload-bar"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <button
          type="button"
          className="submit-time-upload-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={!canEdit}
        >
          Add files
        </button>
        <span className="submit-time-upload-hint">Drop files here to upload.</span>

        {/* Attached file pills */}
        {attachments.length > 0 && (
          <div className="submit-time-attachments-pills">
            {attachments.map(attachment => (
              <span key={attachment.AttachmentId} className="submit-time-attachment-pill">
                <span className="submit-time-attachment-pill-name">
                  {attachment.Description
                    ? attachment.Description + (attachment.FileExtension || '')
                    : attachment.FileName}
                </span>
                {attachment.isUploading ? (
                  <span className="corgee_com_file-uploader-progress-container">
                    <span className="corgee_com_file-uploader-progress-bar submit-time-upload-progress-anim" />
                  </span>
                ) : (
                  canEdit && (
                    <button
                      type="button"
                      className="submit-time-attachment-pill-remove"
                      onClick={() => onAttachmentRemove(attachment.AttachmentId)}
                    >
                      ×
                    </button>
                  )
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={!canEdit}
      />
    </div>
  );
};

export default ReplyEditor;
